import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthLayout from '../components/auth/AuthLayout';
import AuthError from '../components/auth/AuthError';
import AuthSentNotice from '../components/auth/AuthSentNotice';
import EmailRequestForm from '../components/auth/EmailRequestForm';
import { AUTH_LINK_CLASSES } from '../components/auth/authLinkClasses';
import { isEmailSignInLink, storedEmailForSignIn } from '../services/auth.service';
import { authErrorCode } from '../services/authErrors';
import { useAuthStore } from '../store/authStore';
import { useSignInMethods } from '../hooks/useSignInMethods';
import SignInMethodsLoading from '../components/auth/SignInMethodsLoading';
import { Panel } from '../components/ui/Panel';
import { Spinner } from '../components/ui/Spinner';

type LinkState = 'working' | 'needsEmail' | 'failed';

/**
 * `/login/email-link` — requests a passwordless sign-in link, and is also
 * where that link lands. On the device that requested it the stored address
 * finishes sign-in straight away; anywhere else the player confirms it first.
 */
export default function EmailLinkPage() {
    const navigate = useNavigate();
    const { sendEmailSignInLink, completeEmailLinkSignIn, clearError } = useAuthStore();
    const isLink = isEmailSignInLink(window.location.href);
    const methods = useSignInMethods();
    const [sentTo, setSentTo] = useState<string | null>(null);
    const [linkState, setLinkState] = useState<LinkState>('working');
    const started = useRef(false);

    useEffect(() => {
        clearError();
    }, [clearError]);

    const complete = async (email: string, typedByPlayer = false) => {
        if (!typedByPlayer) setLinkState('working');
        try {
            await completeEmailLinkSignIn(email, window.location.href);
            navigate('/', { replace: true });
        } catch (error) {
            // A typed address may just be a typo, and the stored one may belong
            // to a later request for another address. Either way the link is
            // still valid, so keep (or show) the form to retry it.
            if (typedByPlayer) {
                setLinkState('needsEmail');
            } else if (authErrorCode(error) === 'auth/invalid-email') {
                clearError();
                setLinkState('needsEmail');
            } else {
                setLinkState('failed');
            }
        }
    };

    useEffect(() => {
        if (!isLink || started.current) return;
        started.current = true;
        const email = storedEmailForSignIn();
        if (email) {
            void complete(email);
        } else {
            setLinkState('needsEmail');
        }
        // Runs once per link: `complete` changes identity every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLink]);

    const body = () => {
        if (!isLink) {
            if (!methods) return <SignInMethodsLoading />;
            if (!methods.emailLink) {
                return (
                    <p className="text-body text-content-secondary">
                        Signing in with an email link isn&apos;t available right now.
                    </p>
                );
            }
            if (sentTo) {
                return (
                    <AuthSentNotice>
                        We&apos;ve sent a sign-in link to{' '}
                        <span className="font-semibold text-content-primary">{sentTo}</span>. Open it on this device to
                        sign in.
                    </AuthSentNotice>
                );
            }
            return (
                <>
                    <p className="text-body text-content-secondary">
                        We&apos;ll email you a link that signs you in, no password needed.
                    </p>
                    <EmailRequestForm
                        submitLabel="Send sign-in link"
                        onSubmit={async (email) => {
                            await sendEmailSignInLink(email);
                            setSentTo(email);
                        }}
                    />
                </>
            );
        }

        if (linkState === 'needsEmail') {
            return (
                <>
                    <p className="text-body text-content-secondary">
                        Enter the email address you asked for this link with.
                    </p>
                    <AuthError />
                    <EmailRequestForm submitLabel="Sign in" onSubmit={(email) => complete(email, true)} />
                </>
            );
        }

        if (linkState === 'failed') {
            return (
                <>
                    <AuthError />
                    <Link to="/login/email-link" className={AUTH_LINK_CLASSES}>
                        Request a new link
                    </Link>
                </>
            );
        }

        return (
            <p role="status" className="flex items-center gap-2 text-body text-content-secondary">
                <Spinner className="h-4 w-4" /> Signing you in...
            </p>
        );
    };

    return (
        <AuthLayout tagline="Sign in without a password" focus="task">
            <Panel variant="glass" padding="lg" className="flex flex-col gap-3">
                <h2 className="text-heading text-content-primary">
                    {isLink && linkState === 'needsEmail'
                        ? 'Confirm your email'
                        : !isLink && sentTo
                          ? 'Check your inbox'
                          : 'Sign in with an email link'}
                </h2>

                {body()}

                <p className="mt-4 text-body">
                    <Link to="/login" className={AUTH_LINK_CLASSES}>
                        Back to sign in
                    </Link>
                </p>
            </Panel>
        </AuthLayout>
    );
}
